import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import "./App.css";
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement, Tooltip, Legend, Title, Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Tooltip, Legend, Title, Filler);

const API = "https://teafield.maplanka.lk";

/* ─── LAND COVER CLASSES ─────────────────────────────────────────────────── */
const LAND_COVER = {
  1:  { name: "Ineffective",      color: "#8B4513", icon: "⚠️"  },
  2:  { name: "Rubber",           color: "#4a7c3f", icon: "🌲"  },
  3:  { name: "Forestnatural",    color: "#22c55e", icon: "🌳"  },
  4:  { name: "Tea High Vigor",   color: "#00e676", icon: "🍵"  },
  5:  { name: "Tea Med Vigor",    color: "#c6e700", icon: "🍵"  },
  6:  { name: "Tea Low Vigor",    color: "#ff9100", icon: "🍵"  },
  7:  { name: "Agroforest",       color: "#00e5ff", icon: "🌿"  },
  8:  { name: "Buildup",          color: "#e040fb", icon: "🏠"  },
  9:  { name: "Open Area",        color: "#e53935", icon: "🟤"  },
  10: { name: "Water",            color: "#1e88e5", icon: "💧"  },
  11: { name: "Tea Water Stress", color: "#9c27b0", icon: "🍵"  },
};

const PLANT_VARIATION = {
  1: { name: "Very healthy dense canopy", color: "#1b5e20", short: "Very Healthy"  },
  2: { name: "Healthy medium growth",     color: "#388e3c", short: "Healthy"       },
  3: { name: "Water-stressed plants",     color: "#8bc34a", short: "Water Stress"  },
  4: { name: "Nutrient deficiency",       color: "#fdd835", short: "Nutrient Def." },
  5: { name: "Disease-affected plants",   color: "#ff9800", short: "Disease"       },
  6: { name: "Recently pruned areas",     color: "#e53935", short: "Pruned"        },
  7: { name: "Sparse vegetation",         color: "#6d1a1a", short: "Sparse"        },
};

function ndviToVariation(ndvi) {
  if (ndvi >= 0.75) return 1;
  if (ndvi >= 0.60) return 2;
  if (ndvi >= 0.50) return 3;
  if (ndvi >= 0.40) return 4;
  if (ndvi >= 0.32) return 5;
  if (ndvi >= 0.22) return 6;
  return 7;
}

const TEA_CLASSES = [4, 5, 6, 11];

const COLOR_MODES = {
  ndvi:      { label: "NDVI Health",     icon: "📊" },
  landcover: { label: "Land Cover",      icon: "🗺"  },
  variation: { label: "Plant Variation", icon: "🌿"  },
};

const INDEX_META = {
  NDVI:        { color: "#166534", unit: "",   full: "Normalised Difference Vegetation Index", healthy: "0.4–0.85" },
  GNDVI:       { color: "#0e7490", unit: "",   full: "Green NDVI",                             healthy: "0.4–0.8"  },
  SAVI:        { color: "#b45309", unit: "",   full: "Soil Adjusted Vegetation Index",         healthy: "0.3–0.6"  },
  NDWI:        { color: "#1d4ed8", unit: "",   full: "Water Index",                            healthy: "> 0.0"    },
  NDMI:        { color: "#0891b2", unit: "",   full: "Moisture Index",                         healthy: "> 0.1"    },
  NDRE:        { color: "#7c3aed", unit: "",   full: "Red Edge NDVI",                          healthy: "0.2–0.6"  },
  EVI:         { color: "#166534", unit: "",   full: "Enhanced Vegetation Index",              healthy: "0.3–0.8"  },
  MSAVI:       { color: "#365314", unit: "",   full: "Modified Soil Adjusted VI",              healthy: "0.3–0.6"  },
  CIRE:        { color: "#be123c", unit: "",   full: "Chlorophyll Red Edge Index",             healthy: "> 1.5"    },
  LCI:         { color: "#a16207", unit: "",   full: "Leaf Chlorophyll Index",                 healthy: "> 0.1"    },
  VV:          { color: "#6b21a8", unit: "dB", full: "SAR VV Backscatter",                    healthy: "Rising"   },
  VH:          { color: "#7c3aed", unit: "dB", full: "SAR VH Backscatter",                    healthy: "Stable"   },
  VV_minus_VH: { color: "#be185d", unit: "dB", full: "SAR VV−VH Ratio",                       healthy: "Low"      },
};

const DISEASE_RULES = [
  { id: "blister",   name: "Blister Blight",       condition: i => i.CIRE < 1.5 && i.NDRE < 0.3,   treatment: "Apply copper-based fungicide (Bordeaux mixture). Scout affected plots. Re-inspect in 10–14 days." },
  { id: "grey",      name: "Grey / Brown Blight",  condition: i => i.LCI < 0.08 && i.CIRE < 1.8,   treatment: "Apply carbendazim or mancozeb. Remove and burn infected leaves. Improve drainage." },
  { id: "nitrogen",  name: "Nitrogen Deficiency",  condition: i => i.GNDVI < 0.38 && i.LCI < 0.1,  treatment: "Apply urea or sulfate of ammonia (50–75 kg N/ha). Soil test recommended." },
  { id: "water_dis", name: "Water Stress+Disease", condition: i => i.NDWI < -0.05 && i.CIRE < 1.8, treatment: "Irrigate immediately, then apply preventive fungicide." },
];

/* ─── DISEASE / SEVERITY COLORS ─────────────────────────────────────────── */
const SEV_COLOR = { low: "#b45309", medium: "#ea580c", high: "#b91c1c" };
const SEV_WEIGHT = { low: 0.3, medium: 0.6, high: 1.0 };

const DISEASE_COLOR = {
  blister_blight:     "#b91c1c",
  grey_blight:        "#7c3aed",
  brown_blight:       "#b45309",
  red_spider_mite:    "#dc2626",
  thrips:             "#ea580c",
  stem_borer:         "#b91c1c",
  nutrient_deficiency:"#ca8a04",
  other:              "#78716c",
  none:               "#1a8c5b",
};

const DISEASE_LABEL = {
  blister_blight:     "Blister Blight",
  grey_blight:        "Grey Blight",
  brown_blight:       "Brown Blight",
  red_spider_mite:    "Red Spider Mite",
  thrips:             "Thrips",
  stem_borer:         "Stem Borer",
  nutrient_deficiency:"Nutrient Deficiency",
  other:              "Other",
  none:               "None",
};

/* ─── HELPERS ────────────────────────────────────────────────────────────── */
function computeHealth(ndvi, ndmi) {
  if (ndmi != null && ndmi > 0.1 && ndvi > 0.5) return { label: "Harvest Ready",  color: "#0369a1", cls: "health-harvest", icon: "🌿" };
  if (ndvi >= 0.6)  return { label: "Healthy",         color: "#15803d", cls: "health-high",    icon: "✅" };
  if (ndvi >= 0.45) return { label: "Moderate Stress", color: "#92400e", cls: "health-med",     icon: "⚠️" };
  if (ndvi >= 0.35) return { label: "High Risk",       color: "#991b1b", cls: "health-low",     icon: "🔴" };
  return                   { label: "Low Productivity",color: "#7f1d1d", cls: "health-crit",    icon: "☠️" };
}

function calcForecast(ndmi, ndvi, areaHa) {
  if (ndmi == null || ndvi == null || !areaHa) return null;
  const score = Math.max(0, Math.min(1, (ndvi - 0.3) / 0.55)) * Math.max(0, Math.min(1, (ndmi + 0.1) / 0.4));
  const kgPerHa = Math.round(800 + score * 1200);
  return { kgPerHa, totalKg: Math.round(kgPerHa * areaHa), areaHa: areaHa.toFixed(2) };
}

function buildInsight(indices, code) {
  if (!indices) return null;
  const { NDVI: ndvi, CIRE: cire, NDWI: ndwi, NDMI: ndmi, EVI: evi } = indices;
  const p = [];
  if (ndvi >= 0.6 && cire >= 2.0) p.push(`Field ${code} is in strong health — NDVI ${ndvi.toFixed(2)}, CIRE ${cire.toFixed(2)} indicate active productive growth.`);
  else if (ndvi >= 0.45)           p.push(`Field ${code} shows moderate health (NDVI ${ndvi.toFixed(2)}) — acceptable but below peak.`);
  else                             p.push(`Field ${code} is under stress (NDVI ${ndvi.toFixed(2)}) — immediate field investigation recommended.`);
  if (ndmi > 0.1 && ndvi > 0.5)   p.push(`Moisture adequate (NDMI ${ndmi.toFixed(2)}) — approaching harvest readiness.`);
  else if (ndwi < -0.1)            p.push(`Water stress (NDWI ${ndwi.toFixed(2)}) — review irrigation schedule.`);
  if (cire < 1.5) p.push(`Low chlorophyll (CIRE ${cire.toFixed(2)}) — scout for blister blight and review fertiliser.`);
  if (evi < 0.3)  p.push(`Canopy thinning (EVI ${evi.toFixed(2)}) — check for over-pruning or drought.`);
  return p.join(" ");
}

function getFieldColor(field, fieldStats, colorMode) {
  if (colorMode === "ndvi") {
    const v = field.ndvi || 0;
    return v >= 0.6 ? "#22c55e" : v >= 0.35 ? "#f59e0b" : "#ef4444";
  }
  if (colorMode === "variation") {
    const cls = ndviToVariation(field.ndvi || 0);
    return PLANT_VARIATION[cls].color;
  }
  const classes = fieldStats[field.properties?.Field]?.classes || [];
  if (!classes.length) return "#94a3b8";
  const dom = classes.reduce((a, b) => parseFloat(a.percent) > parseFloat(b.percent) ? a : b);
  return LAND_COVER[dom.code]?.color || "#94a3b8";
}

/* ─── DISEASE PIN ICON ───────────────────────────────────────────────────── */
function makeDiseaseIcon(severity) {
  const bg = SEV_COLOR[severity] || "#b91c1c";
  return L.divIcon({
    className: "",
    html: `<div style="
      width:30px;height:30px;border-radius:50% 50% 50% 0;
      background:${bg};border:3px solid white;
      transform:rotate(-45deg);
      box-shadow:0 2px 8px rgba(0,0,0,0.4);
      display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);font-size:13px;margin-top:2px">🦠</span>
    </div>`,
    iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -32],
  });
}

/* ─── SHARED COMPONENTS ──────────────────────────────────────────────────── */
function SH({ children }) {
  return <div className="panel-section-head">{children}</div>;
}

function MetricStrip({ items }) {
  return (
    <div className="metric-strip">
      {items.map((m, i) => (
        <div key={i} className="metric-chip">
          <div className="metric-chip-val" style={{ color: m.color }}>{m.val}</div>
          <div className="metric-chip-lbl">{m.lbl}</div>
        </div>
      ))}
    </div>
  );
}

function HealthBadge({ ndvi, ndmi }) {
  const h = computeHealth(ndvi, ndmi);
  return <span className={`health-badge ${h.cls}`}>{h.icon} {h.label}</span>;
}

/* ─── CLASSIFICATION LEGEND ──────────────────────────────────────────────── */
function ClassificationLegend({ mode }) {
  if (mode === "ndvi") {
    return (
      <div className="cls-legend">
        {[
          { color: "#22c55e", label: "High NDVI",   desc: "≥ 0.60"    },
          { color: "#f59e0b", label: "Medium NDVI", desc: "0.35–0.60" },
          { color: "#ef4444", label: "Low NDVI",    desc: "< 0.35"    },
        ].map(i => (
          <div key={i.label} className="cls-legend-item">
            <span className="cls-dot" style={{ background: i.color }} />
            <span className="cls-name">{i.label}</span>
            <span className="cls-desc">{i.desc}</span>
          </div>
        ))}
      </div>
    );
  }
  if (mode === "variation") {
    return (
      <div className="cls-legend">
        {Object.entries(PLANT_VARIATION).map(([k, v]) => (
          <div key={k} className="cls-legend-item">
            <span className="cls-dot" style={{ background: v.color }} />
            <span className="cls-name">Class {k} — {v.short}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="cls-legend">
      {Object.entries(LAND_COVER).map(([k, v]) => (
        <div key={k} className="cls-legend-item">
          <span className="cls-dot" style={{ background: v.color }} />
          <span className="cls-name">{v.icon} {v.name}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── FIELD CLASS BREAKDOWN ──────────────────────────────────────────────── */
function FieldClassBreakdown({ fieldCode, fieldStats, colorMode }) {
  const classes = fieldStats[fieldCode]?.classes || [];
  if (!classes.length) return <div className="loading-note">⏳ Loading class data…</div>;
  const sorted = [...classes].sort((a, b) => parseFloat(b.percent) - parseFloat(a.percent));
  const topTeaHa = sorted.filter(c => TEA_CLASSES.includes(c.code)).reduce((s, c) => s + parseFloat(c.area_ha), 0);

  if (colorMode === "variation") {
    const variationMap = { 4: 1, 5: 3, 6: 5, 11: 4 };
    const varClasses = sorted.filter(c => TEA_CLASSES.includes(c.code)).map(c => ({
      ...c, varCode: variationMap[c.code] || 4, varMeta: PLANT_VARIATION[variationMap[c.code] || 4],
    }));
    const nonTea = sorted.filter(c => !TEA_CLASSES.includes(c.code));
    return (
      <div className="class-breakdown">
        <SH>🌿 Plant Variation Classification</SH>
        <div className="stacked-bar">
          {varClasses.map(c => <div key={c.code} className="stacked-seg" style={{ width: `${Math.max(2, parseFloat(c.percent))}%`, background: c.varMeta.color }} title={`${c.varMeta.name}: ${parseFloat(c.percent).toFixed(1)}%`} />)}
          {nonTea.map(c => <div key={c.code} className="stacked-seg" style={{ width: `${Math.max(1, parseFloat(c.percent))}%`, background: LAND_COVER[c.code]?.color || "#94a3b8", opacity: 0.4 }} />)}
        </div>
        {varClasses.map(c => (
          <div key={c.code} className="breakdown-row">
            <span className="bd-dot" style={{ background: c.varMeta.color }} />
            <span className="bd-name">{c.varMeta.name}</span>
            <span className="bd-ha">{parseFloat(c.area_ha).toFixed(2)} ha</span>
            <div className="bd-track"><div className="bd-fill" style={{ width: `${Math.min(100, parseFloat(c.percent))}%`, background: c.varMeta.color }} /></div>
            <span className="bd-pct">{parseFloat(c.percent).toFixed(1)}%</span>
          </div>
        ))}
        {nonTea.length > 0 && <div className="bd-other">+{nonTea.length} non-tea classes ({nonTea.reduce((s, c) => s + parseFloat(c.percent), 0).toFixed(1)}%)</div>}
      </div>
    );
  }
  return (
    <div className="class-breakdown">
      <SH>{colorMode === "landcover" ? "🗺 Land Cover Classification" : "📊 Class Breakdown"}</SH>
      <div className="stacked-bar">
        {sorted.map(c => <div key={c.code} className="stacked-seg" style={{ width: `${Math.max(1, parseFloat(c.percent))}%`, background: LAND_COVER[c.code]?.color || "#94a3b8" }} title={`${LAND_COVER[c.code]?.name || c.name}: ${parseFloat(c.percent).toFixed(1)}%`} />)}
      </div>
      {sorted.map(c => {
        const meta = LAND_COVER[c.code];
        const col  = meta?.color || "#94a3b8";
        const pct  = parseFloat(c.percent);
        const isTea = TEA_CLASSES.includes(c.code);
        return (
          <div key={c.code} className={`breakdown-row${isTea ? " bd-tea" : ""}`}>
            <span className="bd-dot" style={{ background: col }} />
            <span className="bd-name">{meta?.icon} {meta?.name || c.name}</span>
            <span className="bd-ha">{parseFloat(c.area_ha).toFixed(2)} ha</span>
            <div className="bd-track"><div className="bd-fill" style={{ width: `${Math.min(100, pct)}%`, background: col }} /></div>
            <span className="bd-pct">{pct.toFixed(1)}%</span>
          </div>
        );
      })}
      {topTeaHa > 0 && <div className="bd-summary">Total tea area: <strong style={{ color: "#15803d" }}>{topTeaHa.toFixed(2)} ha</strong></div>}
    </div>
  );
}

/* ─── ALERTS PANEL ───────────────────────────────────────────────────────── */
function AlertsPanel({ indices }) {
  const [exp, setExp] = useState(null);
  if (!indices) return null;
  const alerts = [];
  if (indices.NDVI  < 0.4)  alerts.push({ key: "prod",   type: "warn",    icon: "⚠️", text: `Productivity alert — NDVI ${indices.NDVI.toFixed(2)} below threshold` });
  if (indices.NDWI  < -0.1) alerts.push({ key: "water",  type: "warn",    icon: "💧", text: `Water stress — NDWI ${indices.NDWI.toFixed(2)} → check irrigation` });
  if (indices.NDMI  < 0.05) alerts.push({ key: "moist",  type: "warn",    icon: "🌾", text: `Harvest delay risk — NDMI ${indices.NDMI?.toFixed(2)} too low` });
  if (indices.EVI   < 0.3)  alerts.push({ key: "canopy", type: "warn",    icon: "🌿", text: `Canopy decline — EVI ${indices.EVI?.toFixed(2)}` });
  DISEASE_RULES.forEach(r => {
    if (r.condition(indices)) alerts.push({ key: r.id, type: "disease", icon: "🦠", text: `${r.name} — CIRE ${indices.CIRE?.toFixed(2)}, LCI ${indices.LCI?.toFixed(2)}`, treatment: r.treatment, ruleName: r.name });
  });
  if (!alerts.length) alerts.push({ key: "ok", type: "ok", icon: "✅", text: "All clear — no critical alerts for this field" });
  return (
    <div className="alerts-panel">
      <SH>🔔 AI Alerts</SH>
      {alerts.map(a => (
        <div key={a.key}>
          <div className={`alert-row alert-${a.type}${a.treatment ? " alert-clickable" : ""}`} onClick={() => a.treatment && setExp(exp === a.key ? null : a.key)}>
            <span className="alert-icon">{a.icon}</span>
            <span className="alert-text">{a.text}</span>
            {a.treatment && <span className="alert-toggle">{exp === a.key ? "▲" : "▼ treatment"}</span>}
          </div>
          {a.treatment && exp === a.key && (
            <div className="treatment-box">
              <div className="treatment-title">💊 {a.ruleName}</div>
              <div className="treatment-body">{a.treatment}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── TREND CARD ─────────────────────────────────────────────────────────── */
function TrendCard({ indexKey, trend }) {
  const meta = INDEX_META[indexKey];
  const vals = trend?.values || [0, 0, 0, 0];
  const avg  = (vals.reduce((a, b) => a + (b || 0), 0) / vals.length).toFixed(4);
  const data = {
    labels: trend?.labels || ["Jan", "Feb", "Mar", "Apr"],
    datasets: [{ label: indexKey, data: vals, borderColor: meta.color, backgroundColor: meta.color + "1a", pointBackgroundColor: meta.color, pointBorderColor: "#fff", pointBorderWidth: 2, pointRadius: 5, tension: 0.4, fill: true, borderWidth: 2.5 }],
  };
  const opts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: "#fff", titleColor: meta.color, bodyColor: "#334155", borderColor: meta.color + "40", borderWidth: 1, callbacks: { label: c => ` ${c.parsed.y.toFixed(4)} ${meta.unit}` } } },
    scales: {
      x: { ticks: { color: "#6b8a74", font: { size: 10 } }, grid: { color: "#edf7f0" } },
      y: { ticks: { color: "#6b8a74", font: { size: 10 } }, grid: { color: "#edf7f0" } },
    },
  };
  return (
    <div className="trend-card">
      <div className="trend-card-top">
        <div><div className="trend-card-key" style={{ color: meta.color }}>{indexKey}</div><div className="trend-card-full">{meta.full}</div></div>
        <div className="trend-card-avg" style={{ borderColor: meta.color + "60", color: meta.color }}>avg {avg}</div>
      </div>
      <div className="trend-card-healthy">Healthy: <strong style={{ color: meta.color }}>{meta.healthy}</strong></div>
      <div className="trend-chart-wrap"><Line data={data} options={opts} /></div>
    </div>
  );
}

/* ─── KOBO OBSERVATION CARD ──────────────────────────────────────────────── */
function ObsCard({ obs, onShowPin }) {
  const [expanded, setExpanded] = useState(false);
  const hasDisease = obs.diseaseDetected === "yes" && obs.diseaseType && obs.diseaseType !== "none";
  const hasGPS     = obs.gpsLocation?.lat && obs.gpsLocation?.lng;
  const sevColor   = SEV_COLOR[obs.diseaseSeverity] || "#b45309";
  const disColor   = DISEASE_COLOR[obs.diseaseType] || "#78716c";

  return (
    <div className="obs-card">
      <div className="obs-card-header" onClick={() => setExpanded(v => !v)}>
        <div className="obs-card-left">
          <span className="obs-observer">👤 {obs.observerName || "—"}</span>
          <span className="obs-date">📅 {obs.obsDate || obs.submittedAt?.split("T")[0] || "—"}</span>
        </div>
        <div className="obs-card-right">
          {obs.cropStage && (
            <span className="obs-badge" style={{ background: "#f0fdf4", color: "#15803d", borderColor: "#bbf7d0" }}>
              🍵 {obs.cropStage.replace(/_/g, " ")}
            </span>
          )}
          {hasDisease && (
            <span className="obs-badge" style={{ background: "#fee2e2", color: disColor, borderColor: disColor + "50" }}>
              🦠 {DISEASE_LABEL[obs.diseaseType] || obs.diseaseType}
            </span>
          )}
          <span className="obs-expand">{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div className="obs-details">
          {obs.cropStage && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🍵</span>
              <span className="obs-detail-label">Growth Stage</span>
              <span className="obs-detail-val" style={{ color: "#15803d" }}>{obs.cropStage.replace(/_/g, " ")}</span>
            </div>
          )}
          {obs.canopyCover && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🌿</span>
              <span className="obs-detail-label">Canopy Cover</span>
              <span className="obs-detail-val">{obs.canopyCover.replace(/_/g, " ")}</span>
            </div>
          )}
          {obs.leafColor && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🍃</span>
              <span className="obs-detail-label">Leaf Color</span>
              <span className="obs-detail-val">{obs.leafColor.replace(/_/g, " ")}</span>
            </div>
          )}
          {hasDisease && (
            <>
              <div className="obs-detail-row">
                <span className="obs-detail-icon">🦠</span>
                <span className="obs-detail-label">Disease / Pest</span>
                <span className="obs-detail-val" style={{ color: disColor, fontWeight: 700 }}>
                  {DISEASE_LABEL[obs.diseaseType] || obs.diseaseType}
                </span>
              </div>
              {obs.diseaseSeverity && (
                <div className="obs-detail-row">
                  <span className="obs-detail-icon">⚠️</span>
                  <span className="obs-detail-label">Severity</span>
                  <span className="obs-detail-val" style={{ color: sevColor, fontWeight: 700 }}>
                    {obs.diseaseSeverity.toUpperCase()}
                  </span>
                </div>
              )}
              {obs.affectedArea && (
                <div className="obs-detail-row">
                  <span className="obs-detail-icon">📐</span>
                  <span className="obs-detail-label">Affected Area</span>
                  <span className="obs-detail-val">{obs.affectedArea}</span>
                </div>
              )}
              {obs.diseaseNotes && (
                <div className="obs-detail-row">
                  <span className="obs-detail-icon">📝</span>
                  <span className="obs-detail-label">Notes</span>
                  <span className="obs-detail-val">{obs.diseaseNotes}</span>
                </div>
              )}
              {hasGPS && (
                <div className="obs-detail-row">
                  <span className="obs-detail-icon">📍</span>
                  <span className="obs-detail-label">GPS Location</span>
                  <span className="obs-detail-val">
                    <button className="pin-btn" onClick={e => { e.stopPropagation(); onShowPin(obs); }}>
                      📍 Show on Map
                    </button>
                    <span className="gps-coords">
                      {obs.gpsLocation.lat.toFixed(5)}, {obs.gpsLocation.lng.toFixed(5)}
                    </span>
                  </span>
                </div>
              )}
            </>
          )}
          {obs.harvestDone === "yes" && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🌾</span>
              <span className="obs-detail-label">Harvest</span>
              <span className="obs-detail-val" style={{ color: "#15803d", fontWeight: 700 }}>
                Done {obs.greenLeafKg ? `· ${obs.greenLeafKg} kg green leaf` : ""}
              </span>
            </div>
          )}
          {obs.irrigationMethod && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">💧</span>
              <span className="obs-detail-label">Irrigation</span>
              <span className="obs-detail-val">{obs.irrigationMethod.replace(/_/g, " ")}</span>
            </div>
          )}
          {obs.fertiliserType && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🧪</span>
              <span className="obs-detail-label">Fertiliser</span>
              <span className="obs-detail-val">{obs.fertiliserType}</span>
            </div>
          )}
          {obs.soilCondition && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">🌍</span>
              <span className="obs-detail-label">Soil</span>
              <span className="obs-detail-val">{obs.soilCondition}</span>
            </div>
          )}
          {obs.generalNotes && (
            <div className="obs-detail-row">
              <span className="obs-detail-icon">📋</span>
              <span className="obs-detail-label">General Notes</span>
              <span className="obs-detail-val">{obs.generalNotes}</span>
            </div>
          )}
          <div className="obs-submitted">
            Submitted: {obs.submittedAt ? new Date(obs.submittedAt).toLocaleString() : "—"}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── HARVEST LOG ─────────────────────────────────────────────────────────── */
function HarvestLog({ fields, koboObs }) {
  const koboEntries = koboObs
    .filter(o => o.harvestDone === "yes" && o.greenLeafKg)
    .map(o => ({
      id:      `kobo-${o.submissionId}`,
      field:   o.fieldCode,
      worker:  o.observerName || "—",
      kgGreen: parseFloat(o.greenLeafKg),
      date:    o.obsDate || o.submittedAt?.split("T")[0] || "—",
      notes:   o.generalNotes || "From KoboToolbox",
      source:  "kobo",
    }));

  const [manualLogs, setManualLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setF] = useState({ field: "", worker: "", kgGreen: "", date: today, notes: "" });
  const codes = [...new Set(fields.map(f => f.properties.Field))].sort();

  const allLogs = [...koboEntries, ...manualLogs].sort((a, b) => b.date.localeCompare(a.date));
  const total = allLogs.reduce((s, l) => s + l.kgGreen, 0);

  const add = () => {
    if (!form.field || !form.kgGreen) return;
    setManualLogs(p => [{ id: Date.now(), ...form, kgGreen: parseFloat(form.kgGreen), source: "manual" }, ...p]);
    setF(f => ({ ...f, field: "", worker: "", kgGreen: "", notes: "" }));
    setOpen(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {allLogs.length > 0 && (
        <MetricStrip items={[
          { val: allLogs.length,             lbl: "Entries",         color: "#334155" },
          { val: `${total.toFixed(0)} kg`,   lbl: "Total green leaf",color: "#15803d" },
          { val: koboEntries.length,          lbl: "From KoboField", color: "#0891b2" },
        ]} />
      )}
      <div className="panel-row-between">
        <SH>🌾 Daily Harvest Log</SH>
        <button className="btn-outline" onClick={() => setOpen(v => !v)}>{open ? "Cancel" : "+ Add Entry"}</button>
      </div>

      {koboEntries.length > 0 && (
        <div className="kobo-harvest-note">
          🟢 {koboEntries.length} entr{koboEntries.length > 1 ? "ies" : "y"} auto-imported from KoboToolbox field observations
        </div>
      )}

      {open && (
        <div className="log-form">
          <div className="form-grid-2">
            <div className="form-field"><label>Field</label>
              <select value={form.field} onChange={e => setF(f => ({ ...f, field: e.target.value }))}>
                <option value="">Select…</option>{codes.map(c => <option key={c} value={c}>Field {c}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Date</label>
              <input type="date" value={form.date} onChange={e => setF(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-field"><label>Worker</label>
              <input placeholder="e.g. Kamala S." value={form.worker} onChange={e => setF(f => ({ ...f, worker: e.target.value }))} />
            </div>
            <div className="form-field"><label>Green leaf (kg)</label>
              <input type="number" placeholder="0.0" step="0.1" min="0" value={form.kgGreen} onChange={e => setF(f => ({ ...f, kgGreen: e.target.value }))} />
            </div>
          </div>
          <div className="form-field"><label>Notes</label>
            <input placeholder="Optional…" value={form.notes} onChange={e => setF(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="form-actions"><button className="btn-primary" onClick={add}>Save Entry</button></div>
        </div>
      )}

      {allLogs.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Date</th><th>Field</th><th>Worker</th><th>kg</th><th>Source</th><th></th></tr></thead>
            <tbody>
              {allLogs.map(l => (
                <tr key={l.id}>
                  <td>{l.date}</td>
                  <td>Field {l.field}</td>
                  <td>{l.worker || "—"}</td>
                  <td className="td-green">{l.kgGreen}</td>
                  <td>
                    <span className={`source-badge ${l.source === "kobo" ? "source-kobo" : "source-manual"}`}>
                      {l.source === "kobo" ? "📱 Kobo" : "✏️ Manual"}
                    </span>
                  </td>
                  <td>
                    {l.source === "manual" && (
                      <button className="del-btn" onClick={() => setManualLogs(p => p.filter(x => x.id !== l.id))}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !open && (
        <div className="empty-state"><div className="empty-icon">🌾</div><div className="empty-msg">No harvest entries yet.</div></div>
      )}
    </div>
  );
}

/* ─── DEMO ACCOUNTS ──────────────────────────────────────────────────────── */
const DEMO_ACCOUNTS = [
  { id: "FO001", name: "Kamala Perera",  role: "Field Officer",  pin: "1234", area: "Zone A"    },
  { id: "FO002", name: "Nuwan Silva",    role: "Senior Officer", pin: "2345", area: "Zone B"    },
  { id: "FO003", name: "Anoma De Silva", role: "Estate Manager", pin: "3456", area: "All Zones" },
  { id: "FO004", name: "Ruwantha Raj",   role: "Field Officer",  pin: "4567", area: "Zone C"    },
];

/* ─── LOGIN PANEL ─────────────────────────────────────────────────────────── */
function LoginPanel({ onClose, currentUser, setCurrentUser }) {
  const [officerId, setOfficerId] = useState("");
  const [pin,       setPin]       = useState("");
  const [error,     setError]     = useState("");
  const [loginLog,  setLoginLog]  = useState([]);

  const now = () => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  const handleLogin = () => {
    setError("");
    const acc = DEMO_ACCOUNTS.find(a => a.id === officerId && a.pin === pin);
    if (!acc) {
      setError("Invalid Officer ID or PIN.");
      setLoginLog(p => [{ type: "fail", name: officerId || "Unknown", time: now() }, ...p]);
      return;
    }
    setCurrentUser(acc);
    setPin("");
    setLoginLog(p => [{ type: "success", name: acc.name, time: now(), detail: `${acc.role} · ${acc.area}` }, ...p]);
  };

  const handleLogout = () => {
    setLoginLog(p => [{ type: "logout", name: currentUser.name, time: now() }, ...p]);
    setCurrentUser(null);
  };

  const initials = name => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="login-overlay">
      <div className="login-panel-header">
        <div className="login-panel-title"><span>🔐</span>{currentUser ? "Field Officer Portal" : "Officer Sign In"}</div>
        <button className="login-panel-close" onClick={onClose}>✕</button>
      </div>
      {currentUser ? (
        <div className="login-profile-strip">
          <div className="login-avatar">{initials(currentUser.name)}</div>
          <div><div className="login-profile-name">{currentUser.name}</div><div className="login-profile-role">{currentUser.role} · {currentUser.area}</div></div>
          <button className="logout-btn" onClick={handleLogout}>Sign out</button>
        </div>
      ) : (
        <div className="login-form-wrap">
          <div className="login-field">
            <label>Officer ID</label>
            <select value={officerId} onChange={e => setOfficerId(e.target.value)}>
              <option value="">Select officer…</option>
              {DEMO_ACCOUNTS.map(a => <option key={a.id} value={a.id}>{a.id} — {a.name}</option>)}
            </select>
          </div>
          <div className="login-field">
            <label>PIN</label>
            <input type="password" placeholder="Enter PIN…" value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} maxLength={6} />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button className="login-btn" onClick={handleLogin}>Sign In →</button>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.25)", textAlign: "center" }}>Demo PINs: FO001→1234 · FO002→2345</div>
        </div>
      )}
      <div className="login-log-wrap">
        <div className="login-log-title">Recent Activity</div>
        {loginLog.length === 0 ? <div className="login-log-empty">No activity yet</div> : loginLog.map((e, i) => (
          <div key={i} className="login-log-entry">
            <div className={`login-log-dot ${e.type}`} />
            <div className="login-log-name">{e.name}</div>
            <div className="login-log-meta">
              <div>{e.type === "success" ? "Signed in" : e.type === "logout" ? "Signed out" : "Failed"}</div>
              <div>{e.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── REPORT GENERATOR ───────────────────────────────────────────────────── */
function generateReport({ field, fieldStats, liveStats, currentUser, koboObs }) {
  const code = field?.properties?.Field;
  if (!code) return;
  const classes = fieldStats[code]?.classes || [];
  const ndvi    = liveStats?.ndvi ?? field.ndvi ?? 0;
  const ndmi    = liveStats?.indices?.NDMI ?? null;
  const indices = liveStats?.indices || {};
  const health  = (() => {
    if (ndmi != null && ndmi > 0.1 && ndvi > 0.5) return { label: "Harvest Ready",  color: "#0369a1", bg: "#e0f2fe" };
    if (ndvi >= 0.6)  return { label: "Healthy",         color: "#166534", bg: "#f0fdf4" };
    if (ndvi >= 0.45) return { label: "Moderate Stress", color: "#92400e", bg: "#fffbeb" };
    if (ndvi >= 0.35) return { label: "High Risk",       color: "#991b1b", bg: "#fff1f2" };
    return               { label: "Low Productivity",   color: "#7f1d1d", bg: "#fff1f2" };
  })();
  const teaHa   = classes.filter(c => [4,5,6,11].includes(c.code)).reduce((s, c) => s + parseFloat(c.area_ha), 0);
  const score   = Math.max(0, Math.min(1, (ndvi - 0.3) / 0.55)) * Math.max(0, Math.min(1, ((ndmi ?? 0) + 0.1) / 0.4));
  const kgPerHa = Math.round(800 + score * 1200);
  const totalKg = Math.round(kgPerHa * teaHa);
  const sorted  = [...classes].sort((a, b) => parseFloat(b.percent) - parseFloat(a.percent));
  const reportDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  const reportTime = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const LC_COLORS = { 1:"#8B4513",2:"#4a7c3f",3:"#22c55e",4:"#00e676",5:"#c6e700",6:"#ff9100",7:"#00e5ff",8:"#e040fb",9:"#e53935",10:"#1e88e5",11:"#9c27b0" };
  const LC_NAMES  = { 1:"Ineffective",2:"Rubber",3:"Forestnatural",4:"Tea High Vigor",5:"Tea Med Vigor",6:"Tea Low Vigor",7:"Agroforest",8:"Buildup",9:"Open Area",10:"Water",11:"Tea Water Stress" };

  const alerts = [];
  if (ndvi < 0.4) alerts.push({ type:"warn", text:`⚠️ Productivity alert — NDVI ${ndvi.toFixed(2)} below threshold` });
  if ((indices.NDWI??0) < -0.1) alerts.push({ type:"warn", text:`💧 Water stress — NDWI ${(indices.NDWI??0).toFixed(2)}` });
  if ((indices.CIRE??0) < 1.5 && (indices.NDRE??0) < 0.3) alerts.push({ type:"disease", text:`🦠 Blister Blight risk — CIRE ${(indices.CIRE??0).toFixed(2)}` });
  if (!alerts.length) alerts.push({ type:"ok", text:"✅ All clear — no critical alerts" });

  // Kobo summary for report
  const koboDisease = koboObs.filter(o => o.diseaseDetected === "yes" && o.diseaseType !== "none");
  const koboHarvest = koboObs.filter(o => o.harvestDone === "yes");
  const totalKgKobo = koboHarvest.reduce((s, o) => s + parseFloat(o.greenLeafKg || 0), 0);

  const koboBlock = koboObs.length > 0 ? `
    <div class="rpt-section-head">📱 KoboToolbox Field Observations (${koboObs.length} reports)</div>
    <div class="rpt-kpi-row">
      <div class="rpt-kpi"><div class="rpt-kpi-val">${koboObs.length}</div><div class="rpt-kpi-lbl">Total Reports</div></div>
      <div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#b91c1c">${koboDisease.length}</div><div class="rpt-kpi-lbl">Disease Alerts</div></div>
      <div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#15803d">${koboHarvest.length}</div><div class="rpt-kpi-lbl">Harvests</div></div>
      <div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#15803d">${totalKgKobo.toFixed(0)} kg</div><div class="rpt-kpi-lbl">Green Leaf</div></div>
    </div>
    ${koboDisease.map(o => `
      <div class="rpt-alert-row rpt-alert-disease">
        🦠 ${DISEASE_LABEL[o.diseaseType]||o.diseaseType} · Severity: ${(o.diseaseSeverity||"?").toUpperCase()} · Observer: ${o.observerName||"?"} · ${o.obsDate||""}
        ${o.gpsLocation ? ` · GPS: ${o.gpsLocation.lat.toFixed(4)}, ${o.gpsLocation.lng.toFixed(4)}` : ""}
      </div>`).join("")}
  ` : "";

  const stackedSegs = sorted.map(c => `<div class="rpt-stacked-seg" style="width:${Math.max(1,parseFloat(c.percent))}%;background:${LC_COLORS[c.code]||"#94a3b8"}"></div>`).join("");
  const classRows   = sorted.map(c => {
    const col = LC_COLORS[c.code]||"#94a3b8", name = LC_NAMES[c.code]||c.name, pct = parseFloat(c.percent), isTea = [4,5,6,11].includes(c.code);
    return `<div class="rpt-class-row" style="${isTea?"background:#f5fbf7;padding:5px 6px;border-radius:5px":""}">
      <div class="rpt-class-dot" style="background:${col}"></div>
      <div class="rpt-class-name">${isTea?"🍵 ":""}${name}</div>
      <div class="rpt-class-bar-wrap"><div class="rpt-class-bar-track"><div class="rpt-class-bar-fill" style="width:${Math.min(100,pct)}%;background:${col}"></div></div></div>
      <div class="rpt-class-ha">${parseFloat(c.area_ha).toFixed(2)} ha</div>
      <div class="rpt-class-pct" style="color:${col}">${pct.toFixed(1)}%</div>
    </div>`;
  }).join("");

  const idxCells = ["NDVI","GNDVI","NDWI","NDMI","NDRE","SAVI","EVI","MSAVI","CIRE","LCI","VV","VH"].map(k =>
    `<div class="rpt-idx"><span class="rpt-idx-key">${k}</span><span class="rpt-idx-val">${indices[k]!=null?indices[k].toFixed(4):"—"}</span></div>`
  ).join("");

  const officerBlock = currentUser ? `
    <div class="rpt-officer-box">
      <div class="rpt-officer-field"><div class="rpt-officer-label">Prepared by</div><div class="rpt-officer-val">${currentUser.name}</div></div>
      <div class="rpt-officer-field"><div class="rpt-officer-label">Role</div><div class="rpt-officer-val">${currentUser.role}</div></div>
      <div class="rpt-officer-field"><div class="rpt-officer-label">Zone</div><div class="rpt-officer-val">${currentUser.area}</div></div>
    </div>` : "";

  const insight = liveStats?.indices ? buildInsight(liveStats.indices, code) : "Load live GEE stats for full AI insight.";

  const html = `
    <div class="rpt-header">
      <div class="rpt-brand"><div class="rpt-brand-icon">🍃</div><div><div class="rpt-brand-name">TeaField Intelligence</div><div class="rpt-brand-sub">Crop Intelligence Dashboard · GEE-Powered</div></div></div>
      <div class="rpt-meta"><div>Field Report</div><div>${reportDate} ${reportTime}</div><div>Mixed S1+S2 · March 2026</div></div>
    </div>
    <div class="rpt-title-band">
      <div><div class="rpt-field-name">Field ${code}</div><div class="rpt-field-sub">Tea Estate · RF Classifier · March 2026</div></div>
      <div class="rpt-health-pill" style="color:${health.color};background:${health.bg};border-color:${health.color}40">${health.label}</div>
    </div>
    ${officerBlock}
    <div class="rpt-section-head">📊 Key Performance Indicators</div>
    <div class="rpt-kpi-row">
      <div class="rpt-kpi"><div class="rpt-kpi-val" style="color:${ndvi>=0.6?"#166534":ndvi>=0.35?"#92400e":"#991b1b"}">${ndvi.toFixed(3)}</div><div class="rpt-kpi-lbl">NDVI</div></div>
      <div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#166534">${teaHa.toFixed(2)} ha</div><div class="rpt-kpi-lbl">Tea Area</div></div>
      ${teaHa>0?`<div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#15803d">${kgPerHa}</div><div class="rpt-kpi-lbl">kg/ha (est.)</div></div><div class="rpt-kpi"><div class="rpt-kpi-val" style="color:#15803d">${totalKg.toLocaleString()}</div><div class="rpt-kpi-lbl">Total kg</div></div>`:""}
    </div>
    ${koboBlock}
    <div class="rpt-section-head">🗺 Land Cover Classification</div>
    <div class="rpt-stacked-bar">${stackedSegs}</div>${classRows}
    <div class="rpt-section-head">🔬 Spectral Indices</div>
    <div class="rpt-idx-grid">${idxCells}</div>
    <div class="rpt-section-head">🔔 Alerts</div>
    ${alerts.map(a=>`<div class="rpt-alert-row rpt-alert-${a.type}">${a.text}</div>`).join("")}
    <div class="rpt-section-head">🤖 AI Insight</div>
    <div class="rpt-insight">${insight}</div>
    <div class="rpt-footer"><div>TeaField Intelligence · Mixed S1+S2 · March 2026</div><div>Generated ${reportDate} ${reportTime} · Confidential</div></div>
  `;

  const el = document.getElementById("report-printable");
  if (el) {
    el.innerHTML = html;
    el.style.display = "block";
    setTimeout(() => { window.print(); setTimeout(() => { el.style.display = "none"; }, 500); }, 120);
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════════════════ */
export default function App() {
  const [fields,        setFields]        = useState([]);
  const [fieldStats,    setFieldStats]    = useState({});
  const [estateStats,   setEstateStats]   = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [liveStats,     setLiveStats]     = useState(null);
  const [liveLoading,   setLiveLoading]   = useState(false);
  const [trends,        setTrends]        = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [activeTab,     setActiveTab]     = useState("overview");
  const [trendIdx,      setTrendIdx]      = useState("NDVI");
  const [search,        setSearch]        = useState("");
  const [diseaseLayer,  setDiseaseLayer]  = useState(false);
  const [colorMode,     setColorMode]     = useState("landcover");
  const [panelW,        setPanelW]        = useState(400);
  const [isResizing,    setIsResizing]    = useState(false);
  const [showLogin,     setShowLogin]     = useState(false);
  const [currentUser,   setCurrentUser]   = useState(null);

  // ── Kobo state ─────────────────────────────────────────────────────────────
  const [allKoboObs,   setAllKoboObs]   = useState([]);   // all submissions
  const [fieldKoboObs, setFieldKoboObs] = useState([]);   // current field only
  const [koboLoading,  setKoboLoading]  = useState(false);
  const [koboError,    setKoboError]    = useState(null);
  const [pinsActive,   setPinsActive]   = useState(false);

  const mapRef        = useRef(null);
  const layersRef     = useRef([]);
  const mergedRef     = useRef([]);
  const diseaseRef    = useRef(null);
  const koboLayersRef = useRef([]);   // GPS disease pins
  const heatLayerRef  = useRef(null);
  const colorModeRef  = useRef(colorMode);
  const fieldStatsRef = useRef(fieldStats);

  useEffect(() => { colorModeRef.current = colorMode; },  [colorMode]);
  useEffect(() => { fieldStatsRef.current = fieldStats; }, [fieldStats]);

  // ── Load ALL kobo on startup ────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/kobo-data`)
      .then(r => setAllKoboObs(r.data.data || []))
      .catch(() => setAllKoboObs([]));
  }, []);

  /* ── Fetch estate data ───────────────────────────────────────────────────── */
  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setEstateStats(r.data)).catch(() => {});
    axios.get(`${API}/field-stats`).then(r => setFieldStats(r.data.data || {})).catch(() => {});
  }, []);

  /* ── Re-colour polygons ──────────────────────────────────────────────────── */
  const recolourAll = useCallback((mode, stats) => {
    layersRef.current.forEach(poly => {
      if (!poly.fieldData) return;
      const col = getFieldColor(poly.fieldData, stats, mode);
      poly.setStyle({ fillColor: col, fillOpacity: 0.65 });
    });
  }, []);

  useEffect(() => {
    if (!layersRef.current.length) return;
    recolourAll(colorMode, fieldStats);
  }, [colorMode, fieldStats, recolourAll]);

  /* ── Clear GPS pins ──────────────────────────────────────────────────────── */
  const clearKoboPins = useCallback(() => {
    const map = mapRef.current; if (!map) return;
    koboLayersRef.current.forEach(l => map.removeLayer(l));
    koboLayersRef.current = [];
    if (heatLayerRef.current) { map.removeLayer(heatLayerRef.current); heatLayerRef.current = null; }
    setPinsActive(false);
  }, []);

  /* ── Show GPS disease pins on map ────────────────────────────────────────── */
  const showDiseasePins = useCallback((observations) => {
    const map = mapRef.current; if (!map) return;
    clearKoboPins();

    const withGPS = observations.filter(o =>
      o.diseaseDetected === "yes" && o.diseaseType !== "none" &&
      o.gpsLocation?.lat && o.gpsLocation?.lng
    );
    if (!withGPS.length) return;

    // Heatmap layer
    const heatPts = withGPS.map(o => [o.gpsLocation.lat, o.gpsLocation.lng, SEV_WEIGHT[o.diseaseSeverity] || 0.5]);
    heatLayerRef.current = L.heatLayer(heatPts, {
      radius: 35, blur: 25, maxZoom: 18,
      gradient: { "0.0": "#22c55e", "0.4": "#fbbf24", "0.7": "#f97316", "1.0": "#dc2626" },
    }).addTo(map);

    // Individual pins
    withGPS.forEach(obs => {
      const { lat, lng } = obs.gpsLocation;
      const marker = L.marker([lat, lng], { icon: makeDiseaseIcon(obs.diseaseSeverity) })
        .addTo(map)
        .bindPopup(`
          <div style="font-family:'JetBrains Mono',monospace;min-width:190px;padding:4px 0">
            <div style="font-weight:800;font-size:13px;color:#b91c1c;margin-bottom:6px">
              🦠 ${DISEASE_LABEL[obs.diseaseType] || obs.diseaseType}
            </div>
            <div style="font-size:11px;color:#5a6e82;margin-bottom:2px">
              Severity: <span style="font-weight:700;color:${SEV_COLOR[obs.diseaseSeverity]||"#b91c1c"}">${(obs.diseaseSeverity||"?").toUpperCase()}</span>
            </div>
            <div style="font-size:11px;color:#5a6e82;margin-bottom:2px">
              Observer: <span style="font-weight:600;color:#0d1b2a">${obs.observerName || "—"}</span>
            </div>
            <div style="font-size:11px;color:#5a6e82;margin-bottom:2px">
              Date: <span style="font-weight:600;color:#0d1b2a">${obs.obsDate || "—"}</span>
            </div>
            ${obs.diseaseNotes ? `<div style="font-size:10px;color:#5a6e82;margin-top:4px;border-top:1px solid #eee;padding-top:4px">${obs.diseaseNotes}</div>` : ""}
            <div style="font-size:10px;color:#9ca3af;margin-top:4px">
              📍 ${lat.toFixed(5)}, ${lng.toFixed(5)}
            </div>
          </div>
        `);
      koboLayersRef.current.push(marker);
    });

    if (koboLayersRef.current.length > 0) {
      map.fitBounds(L.featureGroup(koboLayersRef.current).getBounds(), { padding: [60, 60], maxZoom: 18 });
    }
    setPinsActive(true);
  }, [clearKoboPins]);

  /* ── Show single pin ─────────────────────────────────────────────────────── */
  const showSinglePin = useCallback((obs) => {
    const map = mapRef.current;
    if (!map || !obs.gpsLocation) return;
    clearKoboPins();
    const { lat, lng } = obs.gpsLocation;
    const marker = L.marker([lat, lng], { icon: makeDiseaseIcon(obs.diseaseSeverity) })
      .addTo(map)
      .bindPopup(`<div style="font-family:'JetBrains Mono',monospace;min-width:180px;padding:4px 0">
        <div style="font-weight:800;font-size:13px;color:#b91c1c;margin-bottom:6px">🦠 ${DISEASE_LABEL[obs.diseaseType]||obs.diseaseType}</div>
        <div style="font-size:11px;color:#5a6e82">Severity: <span style="font-weight:700;color:${SEV_COLOR[obs.diseaseSeverity]||"#b91c1c"}">${(obs.diseaseSeverity||"?").toUpperCase()}</span></div>
        <div style="font-size:11px;color:#5a6e82">Observer: <span style="font-weight:600;color:#0d1b2a">${obs.observerName||"—"}</span></div>
      </div>`)
      .openPopup();
    koboLayersRef.current.push(marker);
    map.setView([lat, lng], 18);
    setPinsActive(true);
  }, [clearKoboPins]);

  /* ── Select a field ──────────────────────────────────────────────────────── */
  const selectField = useCallback(field => {
    setSelectedField(field);
    setLiveStats(null);
    setTrends(null);
    setActiveTab("field");
    clearKoboPins();

    const code = field.properties.Field;
    if (!code) return;

    // Load live GEE stats
    setLiveLoading(true);
    axios.get(`${API}/field/${code}/stats`)
      .then(r => { setLiveStats(r.data); setLiveLoading(false); })
      .catch(() => setLiveLoading(false));

    // Load Kobo for this field
    setKoboLoading(true);
    setKoboError(null);
    setFieldKoboObs([]);
    axios.get(`${API}/kobo-data/${code}`)
      .then(r => { setFieldKoboObs(r.data.data || []); setKoboLoading(false); })
      .catch(() => { setKoboError("Could not load field observations."); setKoboLoading(false); });
  }, [clearKoboPins]);

  const loadTrends = useCallback(field => {
    const code = field?.properties?.Field;
    if (!code || trendsLoading) return;
    setTrendsLoading(true);
    setTrends(null);
    axios.get(`${API}/field/${code}/trends`)
      .then(r => { setTrends(r.data.trends); setTrendsLoading(false); })
      .catch(() => setTrendsLoading(false));
  }, [trendsLoading]);

  /* ── Disease overlay (NDVI-based circles) ────────────────────────────────── */
  useEffect(() => {
    if (!mapRef.current) return;
    if (diseaseRef.current) { mapRef.current.removeLayer(diseaseRef.current); diseaseRef.current = null; }
    if (!diseaseLayer || !layersRef.current.length) return;
    const group = L.featureGroup();
    layersRef.current.forEach(p => {
      const f = p.fieldData;
      if (!f || f.ndvi >= 0.4) return;
      const c = p.getBounds().getCenter();
      const d = p.getBounds().getNorthEast().distanceTo(p.getBounds().getSouthWest());
      const r = Math.min(Math.max(d * 0.3, 25), 350);
      const col = f.ndvi < 0.25 ? "#7f1d1d" : f.ndvi < 0.35 ? "#b91c1c" : "#ef4444";
      const circle = L.circle([c.lat, c.lng], { radius: r, color: col, fillColor: col, fillOpacity: 0.28, weight: 2, dashArray: "5 4" });
      circle.bindTooltip(`🦠 ${f.ndvi < 0.25 ? "HIGH RISK" : "MODERATE"} — Field ${p.fieldCode}\nNDVI ${f.ndvi.toFixed(3)}`, { className: "hotspot-tip" });
      group.addLayer(circle);
    });
    group.addTo(mapRef.current);
    diseaseRef.current = group;
  }, [diseaseLayer, fields]);

  /* ── Resize ──────────────────────────────────────────────────────────────── */
  const startResize = useCallback(e => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX, startW = panelW;
    const onMove = ev => setPanelW(Math.max(320, Math.min(600, startW + ev.clientX - startX)));
    const onUp   = () => { setIsResizing(false); document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [panelW]);

  /* ── Init Leaflet ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const map = L.map("leaflet-map", { zoomControl: false }).setView([7.0, 80.6], 13);
    mapRef.current = map;
    L.control.zoom({ position: "bottomright" }).addTo(map);
    const street    = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",   { attribution: "© OpenStreetMap" });
    const satellite = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { attribution: "Tiles © Esri" });
    satellite.addTo(map);
    L.control.layers({ "🗺 Street": street, "🛰 Satellite": satellite }, {}, { position: "topright" }).addTo(map);

    Promise.all([axios.get(`${API}/fields`), axios.get(`${API}/ndvi`)])
      .then(([fR, nR]) => {
        const data  = fR?.data?.data?.features || [];
        const ndviF = nR?.data?.data?.features || [];
        const merged = data.map(f => {
          const m = ndviF.find(n => n.properties.Field === f.properties.Field);
          const v = m?.properties?.ndvi_value ?? 0;
          return { ...f, ndvi: v, health: v >= 0.6 ? "high" : v >= 0.35 ? "medium" : "low" };
        });
        setFields(merged);
        mergedRef.current = merged;

        const group  = L.featureGroup();
        const layers = [];

        merged.forEach(field => {
          if (!field.geometry) return;
          const g    = field.geometry;
          const code = field.properties.Field;
          let ll = [];
          if      (g.type === "Polygon")      ll = g.coordinates[0].map(c => [c[1], c[0]]);
          else if (g.type === "MultiPolygon") ll = g.coordinates[0][0].map(c => [c[1], c[0]]);
          else return;

          const initCol         = getFieldColor(field, fieldStatsRef.current, colorModeRef.current);
          const ndviBorderColor = field.ndvi >= 0.6 ? "#22c55e" : field.ndvi >= 0.35 ? "#f59e0b" : "#ef4444";

          const poly = L.polygon(ll, { color: ndviBorderColor, weight: 1.5, fillColor: initCol, fillOpacity: 0.65 }).addTo(map);
          poly.fieldCode = code;
          poly.fieldData = field;

          poly.bindPopup(`
            <div style="font-family:'JetBrains Mono',monospace;min-width:200px">
              <div style="font-weight:700;font-size:14px;color:#166534;margin-bottom:8px;border-bottom:1px solid #d4e8da;padding-bottom:6px">Field ${code}</div>
              <div style="display:flex;gap:8px;margin-bottom:6px">
                <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:6px 12px;text-align:center;flex:1">
                  <div style="font-size:15px;font-weight:700;color:${ndviBorderColor}">${field.ndvi.toFixed(3)}</div>
                  <div style="font-size:9px;color:#9bb8a3;margin-top:2px">NDVI</div>
                </div>
                <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:6px 12px;text-align:center;flex:1">
                  <div style="font-size:11px;font-weight:700;color:${ndviBorderColor}">${field.health.toUpperCase()}</div>
                  <div style="font-size:9px;color:#9bb8a3;margin-top:2px">HEALTH</div>
                </div>
              </div>
              <div style="font-size:10px;color:#6b8a74;text-align:center">Click panel → Field tab for full analysis</div>
            </div>
          `);

          poly.on("click", () => {
            layers.forEach(p => p.setStyle({ color: p.fieldData.ndvi >= 0.6 ? "#22c55e" : p.fieldData.ndvi >= 0.35 ? "#f59e0b" : "#ef4444", weight: 1.5 }));
            poly.setStyle({ color: "#FFD700", weight: 3 });
            map.fitBounds(poly.getBounds(), { padding: [60, 60] });
            selectField(field);
          });

          layers.push(poly);
          group.addLayer(poly);
        });

        layersRef.current = layers;
        if (layers.length) map.fitBounds(group.getBounds(), { padding: [20, 20] });
        setTimeout(() => recolourAll(colorModeRef.current, fieldStatsRef.current), 1500);

        window.searchField = val => {
          if (!val) return;
          const s = val.toString().trim().toUpperCase();
          const found = mergedRef.current.find(f => String(f.properties.Field).toUpperCase() === s || String(f.properties.Field).toUpperCase().includes(s));
          if (!found) { alert("Field not found: " + val); return; }
          const layer = layersRef.current.find(p => String(p.fieldCode) === String(found.properties.Field));
          if (layer) {
            layersRef.current.forEach(p => p.setStyle({ weight: 1.5 }));
            layer.setStyle({ color: "#FFD700", weight: 3 });
            mapRef.current.fitBounds(layer.getBounds(), { padding: [60, 60] });
            layer.openPopup();
          }
          selectField(found);
        };
      }).catch(err => console.error("API ERROR:", err));
    return () => map.remove();
  }, [selectField, recolourAll]);

  /* ── Derived ─────────────────────────────────────────────────────────────── */
  const code      = selectedField?.properties?.Field;
  const curNdvi   = liveStats ? liveStats.ndvi : (selectedField?.ndvi || 0);
  const curNdmi   = liveStats?.indices?.NDMI ?? null;
  const forecast  = liveStats ? calcForecast(curNdmi, curNdvi, (fieldStats[code]?.classes || []).filter(c => TEA_CLASSES.includes(c.code)).reduce((s, c) => s + parseFloat(c.area_ha), 0)) : null;
  const insight   = liveStats?.indices ? buildInsight(liveStats.indices, code) : null;
  const ndviColor = selectedField ? (selectedField.ndvi >= 0.6 ? "#15803d" : selectedField.ndvi >= 0.35 ? "#92400e" : "#991b1b") : "#15803d";

  // Kobo summary for current field
  const koboSummary = {
    total:       fieldKoboObs.length,
    diseases:    fieldKoboObs.filter(o => o.diseaseDetected === "yes" && o.diseaseType !== "none").length,
    harvests:    fieldKoboObs.filter(o => o.harvestDone === "yes").length,
    totalKg:     fieldKoboObs.filter(o => o.greenLeafKg).reduce((s, o) => s + parseFloat(o.greenLeafKg), 0).toFixed(1),
    withGPS:     fieldKoboObs.filter(o => o.diseaseDetected === "yes" && o.gpsLocation?.lat).length,
  };

  const idxKeys = ["NDVI", "GNDVI", "SAVI", "NDWI"];
  const barData = {
    labels: idxKeys,
    datasets: [{ label: "Value", data: idxKeys.map(k => liveStats?.indices?.[k] || 0), backgroundColor: idxKeys.map(k => INDEX_META[k].color), borderRadius: 6, borderWidth: 0 }],
  };
  const barOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: "#fff", titleColor: "#0d1b2a", bodyColor: "#334155", borderColor: "#d4e8da", borderWidth: 1 } },
    scales: {
      x: { ticks: { color: "#6b8a74", font: { size: 10 } }, grid: { color: "#edf7f0" } },
      y: { max: 1, ticks: { color: "#6b8a74", font: { size: 10 } }, grid: { color: "#edf7f0" } },
    },
  };
  const classes  = code ? (fieldStats[code]?.classes || []) : [];
  const dougData = { labels: classes.map(c => c.name), datasets: [{ data: classes.map(c => parseFloat(c.percent)), backgroundColor: classes.map(c => LAND_COVER[c.code]?.color || "#94a3b8"), borderColor: "#fff", borderWidth: 2 }] };
  const dougOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { color: "#6b8a74", font: { size: 9 }, padding: 8, boxWidth: 10 } }, tooltip: { backgroundColor: "#fff", titleColor: "#166534", bodyColor: "#334155", borderColor: "#d4e8da", borderWidth: 1 } }, cutout: "52%" };

  const filteredFields = fields.filter(f => !search || String(f.properties.Field).toLowerCase().includes(search.toLowerCase()));

  const NAV = [
    { id: "overview", icon: "🏢", label: "Overview"  },
    { id: "field",    icon: "📋", label: "Field"     },
    { id: "kobo",     icon: "📱", label: "Field Obs" },
    { id: "trends",   icon: "📈", label: "Trends"    },
    { id: "harvest",  icon: "🌾", label: "Harvest"   },
  ];

  const jumpTo = field => {
    selectField(field);
    const layer = layersRef.current.find(p => String(p.fieldCode) === String(field.properties.Field));
    if (layer && mapRef.current) {
      layersRef.current.forEach(p => p.setStyle({ weight: 1.5 }));
      layer.setStyle({ color: "#FFD700", weight: 3 });
      mapRef.current.fitBounds(layer.getBounds(), { padding: [60, 60] });
      layer.openPopup();
    }
  };

  /* ─── RENDER ─────────────────────────────────────────────────────────────── */
  return (
    <div className="app-root">

      {/* TOP BAR */}
      <header className="topbar">
        <div className="topbar-brand">
          <span className="brand-leaf">🍃</span>
          <div><div className="brand-name">TeaField</div><div className="brand-sub">Crop Intelligence Dashboard</div></div>
        </div>
        <div className="topbar-search">
          <span className="search-icon">🔎</span>
          <input className="search-input" placeholder="Search field code…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && window.searchField?.(search)} />
          <button className="search-btn" onClick={() => window.searchField?.(search)}>Search</button>
        </div>
        <div className="topbar-badges">
          <span className="tbadge">{fields.length} Fields</span>
          {estateStats && <span className="tbadge tbadge-tea">{estateStats.totalTeaHa} ha Tea</span>}
          <span className="tbadge" style={{ color: "#b45309", background: "#fef3c7", borderColor: "#fde68a" }}>
            {allKoboObs.length} Field Reports
          </span>
          {pinsActive && (
            <button className="pest-map-clear-btn" onClick={clearKoboPins}>✕ Clear Pins</button>
          )}
          <span className="tbadge tbadge-live">● GEE Live</span>
        </div>
        <div className="topbar-actions">
          <button
            className={`topbar-action-btn btn-report${!selectedField ? " no-field" : ""}`}
            title={selectedField ? `Report for Field ${code}` : "Select a field first"}
            onClick={() => { if (!selectedField) return; generateReport({ field: selectedField, fieldStats, liveStats, currentUser, koboObs: fieldKoboObs }); }}
          >
            📄 Report
          </button>
          <button
            className={`topbar-action-btn btn-login${currentUser ? " logged-in" : ""}`}
            onClick={() => setShowLogin(v => !v)}
          >
            {currentUser ? `👤 ${currentUser.name.split(" ")[0]}` : "🔐 Login"}
          </button>
        </div>
      </header>

      <div className="app-body">
        <aside className="side-panel" style={{ width: panelW }}>
          <nav className="panel-nav">
            {NAV.map(t => (
              <button key={t.id} className={`panel-nav-btn${activeTab === t.id ? " active" : ""}`}
                onClick={() => { setActiveTab(t.id); if (t.id === "trends" && selectedField && !trends) loadTrends(selectedField); }}>
                <span className="pnb-icon">{t.icon}</span>
                <span className="pnb-label">{t.label}</span>
              </button>
            ))}
          </nav>

          <div className="panel-scroll">

            {/* ══ OVERVIEW TAB ══════════════════════════════════════════════ */}
            {activeTab === "overview" && (
              <div className="panel-content">
                <MetricStrip items={[
                  { val: fields.length,                            lbl: "Total fields", color: "#1a2e20" },
                  { val: estateStats?.totalTeaHa ?? "—",           lbl: "Tea ha",       color: "#15803d" },
                  { val: fields.filter(f => f.ndvi >= 0.6).length, lbl: "High NDVI",   color: "#15803d" },
                  { val: fields.filter(f => f.ndvi < 0.35).length, lbl: "Low NDVI",    color: "#991b1b" },
                ]} />

                <SH>🎨 Map Classification</SH>
                <div className="mode-switcher">
                  {Object.entries(COLOR_MODES).map(([key, m]) => (
                    <button key={key} className={`mode-btn${colorMode === key ? " active" : ""}`} onClick={() => setColorMode(key)}>
                      <span className="mode-icon">{m.icon}</span>
                      <span className="mode-label">{m.label}</span>
                    </button>
                  ))}
                </div>

                <div className="legend-card">
                  <div className="legend-card-title">{COLOR_MODES[colorMode].icon} {COLOR_MODES[colorMode].label} Legend</div>
                  <ClassificationLegend mode={colorMode} />
                </div>

                <SH>🗺 Overlays</SH>
                <div className="toggle-row">
                  <label className="toggle-label">
                    <input type="checkbox" checked={diseaseLayer} onChange={e => setDiseaseLayer(e.target.checked)} />
                    <span>🦠 Disease / stress hotspot overlay</span>
                  </label>
                  {diseaseLayer && fields.filter(f => f.ndvi < 0.4).length > 0 && (
                    <span className="at-risk-pill">{fields.filter(f => f.ndvi < 0.4).length} at risk</span>
                  )}
                </div>

                {/* Estate-wide Kobo GPS pins toggle */}
                {allKoboObs.filter(o => o.gpsLocation?.lat && o.diseaseDetected === "yes").length > 0 && (
                  <div className="toggle-row" style={{ marginTop: 6 }}>
                    <label className="toggle-label">
                      <input type="checkbox" checked={pinsActive}
                        onChange={e => e.target.checked ? showDiseasePins(allKoboObs) : clearKoboPins()} />
                      <span>📍 Show all disease GPS pins ({allKoboObs.filter(o => o.gpsLocation?.lat && o.diseaseDetected === "yes").length} reports)</span>
                    </label>
                  </div>
                )}

                <SH>📋 Field List — click to analyse</SH>
                <div className="field-list">
                  {filteredFields.map((f, i) => {
                    const fc  = f.properties.Field;
                    const nc  = f.ndvi >= 0.6 ? "#15803d" : f.ndvi >= 0.35 ? "#92400e" : "#991b1b";
                    const fillCol   = getFieldColor(f, fieldStats, colorMode);
                    const varCls    = colorMode === "variation" ? PLANT_VARIATION[ndviToVariation(f.ndvi)] : null;
                    const lcCls     = colorMode === "landcover" ? (() => { const cls = fieldStats[fc]?.classes || []; if (!cls.length) return null; return cls.reduce((a, b) => parseFloat(a.percent) > parseFloat(b.percent) ? a : b); })() : null;
                    const classLabel = varCls ? varCls.short : lcCls ? (LAND_COVER[lcCls.code]?.name || lcCls.name) : null;
                    const obsCount  = allKoboObs.filter(o => String(o.fieldCode).toUpperCase() === String(fc).toUpperCase()).length;
                    const hasDiseaseGPS = allKoboObs.some(o => String(o.fieldCode).toUpperCase() === String(fc).toUpperCase() && o.diseaseDetected === "yes" && o.gpsLocation?.lat);
                    return (
                      <div key={i} className="field-card" onClick={() => jumpTo(f)}>
                        <div className="field-card-row1">
                          <span className="field-dot" style={{ background: fillCol, width: 12, height: 12, borderRadius: 3 }} />
                          <span className="field-name">Field {fc}</span>
                          <HealthBadge ndvi={f.ndvi} ndmi={null} />
                        </div>
                        <div className="field-card-row2">
                          <span className="field-class">{classLabel || "Loading…"}</span>
                          <span className="field-ndvi-val" style={{ color: nc }}>NDVI {f.ndvi.toFixed(3)}</span>
                          {obsCount > 0 && <span style={{ color: "#b45309", fontWeight: 700, fontSize: 10 }}>📋 {obsCount}</span>}
                          {hasDiseaseGPS && <span style={{ color: "#b91c1c", fontWeight: 700, fontSize: 10 }}>🦠 GPS</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="panel-footer">Mixed S1+S2 · KoboToolbox · RF Classifier · March 2026</div>
              </div>
            )}

            {/* ══ FIELD TAB ════════════════════════════════════════════════ */}
            {activeTab === "field" && (
              <div className="panel-content">
                {selectedField ? (
                  <>
                    <div className="field-header-card" style={{ borderColor: ndviColor + "50" }}>
                      <div className="fhc-title">Field {code}</div>
                      <div className="fhc-sub">Tea Estate · March 2026</div>
                      <div className="fhc-stats">
                        <div className="fhc-stat">
                          <div className="fhc-stat-val" style={{ color: ndviColor }}>{liveLoading ? "…" : curNdvi.toFixed(3)}</div>
                          <div className="fhc-stat-lbl">NDVI</div>
                        </div>
                        <div className="fhc-stat">
                          <HealthBadge ndvi={curNdvi} ndmi={curNdmi} />
                          <div className="fhc-stat-lbl" style={{ marginTop: 5 }}>Health</div>
                        </div>
                        <div className="fhc-stat">
                          <div className="fhc-stat-val">{(() => { const cls = fieldStats[code]?.classes || []; return cls.filter(c => TEA_CLASSES.includes(c.code)).reduce((s, c) => s + parseFloat(c.area_ha), 0).toFixed(2); })()} ha</div>
                          <div className="fhc-stat-lbl">Tea area</div>
                        </div>
                      </div>
                    </div>

                    <FieldClassBreakdown fieldCode={code} fieldStats={fieldStats} colorMode={colorMode} />
                    {liveStats?.indices && <AlertsPanel indices={liveStats.indices} />}
                    {insight && <div className="insight-box"><div className="insight-title">🤖 AI Insight</div><div className="insight-body">{insight}</div></div>}

                    {forecast && <>
                      <SH>🌾 Yield Forecast</SH>
                      <MetricStrip items={[
                        { val: forecast.kgPerHa, lbl: "kg / ha (est.)", color: "#15803d" },
                        { val: forecast.totalKg, lbl: "Total kg cycle", color: "#15803d" },
                        { val: forecast.areaHa,  lbl: "Tea ha",         color: "#1a2e20" },
                      ]} />
                      <div className="data-note">Estimate based on NDMI × NDVI model</div>
                    </>}

                    {liveStats && <>
                      <SH>📊 Vegetation Indices</SH>
                      <div className="chart-box" style={{ height: 180 }}><Bar data={barData} options={barOpts} /></div>
                    </>}
                    {liveLoading && <div className="loading-note">⏳ Loading live GEE values…</div>}

                    {liveStats?.indices && <>
                      <SH>🔬 Extended Indices</SH>
                      <div className="index-grid">
                        {["NDMI","NDRE","EVI","MSAVI","CIRE","LCI"].map(k => (
                          <div key={k} className="index-row">
                            <span className="index-key" style={{ color: INDEX_META[k]?.color }}>{k}</span>
                            <span className="index-val">{liveStats.indices[k] != null ? liveStats.indices[k].toFixed(4) : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </>}

                    {liveStats?.indices && <>
                      <SH>📡 SAR Backscatter</SH>
                      <div className="index-grid">
                        {["VV","VH","VV_minus_VH","VV_contrast","VV_entropy","VV_homogeneity"].map(k => (
                          <div key={k} className="index-row">
                            <span className="index-key">{k.replace(/_/g, " ")}</span>
                            <span className="index-val">{liveStats.indices[k] != null ? liveStats.indices[k].toFixed(4) : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </>}

                    {classes.length > 0 && <>
                      <SH>🥧 Land Cover Distribution</SH>
                      <div className="chart-box" style={{ height: 240 }}><Doughnut data={dougData} options={dougOpts} /></div>
                    </>}

                    <div className="data-note">Mixed S1+S2 · RF Classifier · March 2026</div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">🍃</div>
                    <div className="empty-msg">Click any field on the map to view full analysis.</div>
                  </div>
                )}
              </div>
            )}

            {/* ══ FIELD OBSERVATIONS TAB (KOBO) ════════════════════════════ */}
            {activeTab === "kobo" && (
              <div className="panel-content">
                {selectedField ? (
                  <>
                    {/* Summary strip */}
                    <div className="selected-box" style={{ borderColor: "#b4530960" }}>
                      <div className="selected-name">Field {code} — Observations</div>
                      <div className="selected-meta"><span>KoboToolbox · Field Reports</span></div>
                      <div className="selected-stats" style={{ marginTop: 8 }}>
                        <div className="sel-stat"><div className="sel-stat-val" style={{ color: "#b45309" }}>{koboSummary.total}</div><div className="sel-stat-lbl">Reports</div></div>
                        <div className="sel-stat"><div className="sel-stat-val" style={{ color: "#b91c1c" }}>{koboSummary.diseases}</div><div className="sel-stat-lbl">Disease Alerts</div></div>
                        <div className="sel-stat"><div className="sel-stat-val" style={{ color: "#15803d" }}>{koboSummary.harvests}</div><div className="sel-stat-lbl">Harvests</div></div>
                        <div className="sel-stat"><div className="sel-stat-val" style={{ color: "#15803d", fontSize: 12 }}>{koboSummary.totalKg}kg</div><div className="sel-stat-lbl">Green Leaf</div></div>
                      </div>
                    </div>

                    {/* GPS map button */}
                    {koboSummary.withGPS > 0 && (
                      <div className="pest-map-section">
                        <div className="pest-map-info">
                          <span className="pest-map-count">🦠 {koboSummary.withGPS} disease GPS location{koboSummary.withGPS > 1 ? "s" : ""} recorded</span>
                        </div>
                        <button
                          className={`pest-map-btn ${pinsActive ? "active" : ""}`}
                          onClick={() => pinsActive ? clearKoboPins() : showDiseasePins(fieldKoboObs)}
                        >
                          {pinsActive ? "🗺 Hide Disease Locations" : "🗺 View Disease Locations on Map"}
                        </button>
                        {pinsActive && (
                          <div className="pest-map-legend">
                            <span className="pest-legend-title">Severity:</span>
                            {[["low","#b45309","Low"],["medium","#ea580c","Medium"],["high","#b91c1c","High"]].map(([k,c,l]) => (
                              <span key={k} className="pest-legend-item">
                                <span style={{ display:"inline-block",width:10,height:10,borderRadius:"50%",backgroundColor:c,marginRight:4 }}/>
                                {l}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <SH>📋 Field Observations — KoboToolbox</SH>

                    {koboLoading && <div className="empty-state"><div className="empty-icon">⏳</div><div className="empty-msg">Loading observations…</div></div>}
                    {koboError  && <div className="empty-state"><div className="empty-icon">❌</div><div className="empty-msg">{koboError}</div></div>}
                    {!koboLoading && !koboError && fieldKoboObs.length === 0 && (
                      <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <div className="empty-msg">No observations yet for Field {code}. Submit via KoboCollect.</div>
                      </div>
                    )}
                    {!koboLoading && fieldKoboObs.length > 0 && (
                      <div className="obs-list">
                        {fieldKoboObs.map((obs, i) => (
                          <ObsCard key={obs.submissionId || i} obs={obs} onShowPin={showSinglePin} />
                        ))}
                      </div>
                    )}
                    <div className="data-note">KoboToolbox · Tap card to expand · 📍 GPS disease pins</div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">📱</div>
                    <div className="empty-msg">Select a field on the map to view KoboToolbox observations.</div>
                  </div>
                )}
              </div>
            )}

            {/* ══ TRENDS TAB ════════════════════════════════════════════════ */}
            {activeTab === "trends" && (
              <div className="panel-content">
                {selectedField ? (
                  <>
                    <SH>📈 Vegetation & SAR Trends — Jan–Apr 2026</SH>
                    <div className="trends-context">Monthly means across <strong>Field {code}</strong>.</div>
                    <div className="index-pill-row">
                      {Object.keys(INDEX_META).map(k => (
                        <button key={k} className={`index-pill${trendIdx === k ? " active" : ""}`}
                          style={trendIdx === k ? { borderColor: INDEX_META[k].color + "80", color: INDEX_META[k].color, background: INDEX_META[k].color + "12" } : {}}
                          onClick={() => { setTrendIdx(k); if (!trends && !trendsLoading) loadTrends(selectedField); }}>
                          {k}
                        </button>
                      ))}
                    </div>
                    {trendsLoading && <div className="loading-note">⏳ Loading from GEE… (20–40 s)</div>}
                    {trends && !trendsLoading && <TrendCard indexKey={trendIdx} trend={trends[trendIdx]} />}
                    {!trends && !trendsLoading && <div className="empty-state"><div className="empty-icon">📈</div><div className="empty-msg">Click any index above to load monthly trends.</div></div>}
                    <div className="data-note">Mixed S1+S2 · 10 m · Jan–Apr 2026</div>
                  </>
                ) : (
                  <div className="empty-state"><div className="empty-icon">📈</div><div className="empty-msg">Select a field first.</div></div>
                )}
              </div>
            )}

            {/* ══ HARVEST TAB (with Kobo auto-import) ══════════════════════ */}
            {activeTab === "harvest" && (
              <div className="panel-content">
                <HarvestLog fields={fields} koboObs={fieldKoboObs} />
              </div>
            )}

          </div>
        </aside>

        {/* Resize handle */}
        <div className={`resize-handle${isResizing ? " resizing" : ""}`} onMouseDown={startResize} title="Drag to resize" />

        {/* MAP AREA */}
        <div className="map-area" style={{ position: "relative" }}>
          <div id="leaflet-map" style={{ width: "100%", height: "100%" }} />
          {showLogin && (
            <LoginPanel onClose={() => setShowLogin(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />
          )}
        </div>
      </div>

      {/* Hidden print container */}
      <div id="report-printable" />
    </div>
  );
}